import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Layers, Play, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AutoInvoiceTab = ({ 
  autoInvoiceSettings,
  setAutoInvoiceSettings,
  autoInvoiceLogs,
  runningAutoInvoice,
  setRunningAutoInvoice,
  getAuthHeaders,
  onRefresh 
}) => {

  const handleToggleAutoInvoice = async (enabled) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/auto-invoice?enabled=${enabled}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        setAutoInvoiceSettings({ ...autoInvoiceSettings, enabled });
        toast.success(`Auto-invoicing ${enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      toast.error('Failed to update auto-invoice settings');
    }
  };

  const handleRunAutoInvoiceNow = async () => {
    setRunningAutoInvoice(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/auto-invoice/run-now`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Invoice generation started. Check logs in a few moments.');
        setTimeout(onRefresh, 5000);
      }
    } catch (error) {
      toast.error('Failed to run auto-invoice');
    } finally {
      setRunningAutoInvoice(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Automated Weekly Invoicing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Auto-Invoice</Label>
              <p className="text-sm text-muted-foreground">
                Automatically generate invoices every Monday at 6 AM for completed work orders
              </p>
            </div>
            <Switch
              checked={autoInvoiceSettings?.enabled || false}
              onCheckedChange={handleToggleAutoInvoice}
            />
          </div>
          
          <div className="border-t pt-4">
            <p className="text-sm text-muted-foreground mb-4">
              Manually trigger invoice generation for all completed, unbilled work orders.
            </p>
            <Button onClick={handleRunAutoInvoiceNow} disabled={runningAutoInvoice}>
              {runningAutoInvoice ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Generate Invoices Now
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Auto-Invoice Runs</CardTitle>
        </CardHeader>
        <CardContent>
          {autoInvoiceLogs && autoInvoiceLogs.length > 0 ? (
            <div className="space-y-3">
              {autoInvoiceLogs.map((log, index) => (
                <div key={index} className="p-3 rounded-lg border bg-muted/50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">
                      {new Date(log.run_at).toLocaleString()}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      log.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {log.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Generated {log.invoices_created || 0} invoices for {log.work_orders_processed || 0} work orders
                  </p>
                  {log.details && log.details.length > 0 && (
                    <div className="mt-2 text-sm">
                      {log.details.map((inv, i) => (
                        <div key={i} className="text-muted-foreground">
                          • {inv.invoice_number} - {inv.client_name}: {inv.work_orders_count} orders
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No auto-invoice runs yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AutoInvoiceTab;
